import { FC } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Row, Column } from 'further-ui/components/Layout';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
  toPercentage,
} from 'further-ui/utils';
import CapitalActionsSummaryStat from './CapitalActionsSummaryStat';
import CapitalProgress from './CapitalProgress';

const Summary = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  gap: theme.spacing(theme.spacingSizes.lg),
}));

type CapitalActionsSummaryContentProps = {
  baseCurrency?: string;
  totalCommittedCapitalLcy: number;
  totalCapitalCalledLcy: number;
  totalPaidInCapitalLcy: number;
  recallableDistributionsLcy: number;
  availableToCallLcy: number;
  totalInvestorsWithOutstandingCalls: number;
  totalUnpaidCalledCapitalLcy: number;
};

const CapitalActionsSummary: FC<CapitalActionsSummaryContentProps> = ({
  baseCurrency,
  totalCommittedCapitalLcy,
  totalCapitalCalledLcy,
  totalPaidInCapitalLcy,
  recallableDistributionsLcy,
  availableToCallLcy,
  totalInvestorsWithOutstandingCalls,
  totalUnpaidCalledCapitalLcy,
}) => {
  const paidInPercentage = toPercentage(
    totalPaidInCapitalLcy,
    totalCommittedCapitalLcy,
  );

  const totalCapitalCalledPercentage = toPercentage(
    totalCapitalCalledLcy,
    totalCommittedCapitalLcy,
  );

  return (
    <Column spacing="lg">
      <Column spacing="xs">
        <Typography variant="h5">Vehicle summary</Typography>
        <Typography variant="caption">
          Excluding outside commitment capital
        </Typography>
      </Column>
      <Summary>
        <Row flexWrap="wrap" spacing="lg" justify="space-between">
          <CapitalActionsSummaryStat
            label="Total committed capital"
            value={numberToCurrencyString(totalCommittedCapitalLcy, {
              currency: baseCurrency,
              fallback: '--',
            })}
            description={`As at ${new CalendarDay(new Date()).toReadable()}`}
          />
          <CapitalActionsSummaryStat
            label="Paid-in capital"
            value={numberToCurrencyString(totalPaidInCapitalLcy, {
              currency: baseCurrency,
              fallback: '--',
            })}
            description={numberToPercentageString(paidInPercentage, {
              accuracy: 2,
              noMultiply: true,
              forceDecimals: true,
            })}
          />
          <CapitalActionsSummaryStat
            label="Unpaid called capital"
            value={numberToCurrencyString(totalUnpaidCalledCapitalLcy, {
              currency: baseCurrency,
              fallback: '--',
            })}
            description={`${totalInvestorsWithOutstandingCalls} ${pluralize(
              'investor',
              totalInvestorsWithOutstandingCalls,
            )}`}
          />
          <CapitalActionsSummaryStat
            label="Recallable distributions"
            value={numberToCurrencyString(recallableDistributionsLcy, {
              currency: baseCurrency,
              fallback: '--',
            })}
            description={numberToPercentageString(
              toPercentage(
                recallableDistributionsLcy,
                totalCommittedCapitalLcy,
              ),
              {
                accuracy: 2,
                noMultiply: true,
                forceDecimals: true,
              },
            )}
          />
          <CapitalActionsSummaryStat
            label="Available to call"
            value={numberToCurrencyString(availableToCallLcy, {
              currency: baseCurrency,
              fallback: '--',
            })}
            description={numberToPercentageString(
              toPercentage(availableToCallLcy, totalCommittedCapitalLcy),
              {
                accuracy: 2,
                noMultiply: true,
                forceDecimals: true,
              },
            )}
          />
        </Row>
        <Divider />
        <Column spacing="sm">
          <CapitalProgress
            valueBuffer={totalCapitalCalledPercentage}
            value={paidInPercentage}
          />
          <Typography variant="h6" color="grey.600">
            {numberToCurrencyString(totalCapitalCalledLcy, {
              currency: baseCurrency,
            })}{' '}
            called (
            {numberToPercentageString(totalCapitalCalledPercentage, {
              accuracy: 2,
              noMultiply: true,
              forceDecimals: true,
            })}
            )
          </Typography>
        </Column>
      </Summary>
    </Column>
  );
};

export default CapitalActionsSummary;
